import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { threadsApi } from '../services/api';
import { NOTES_QUERY_KEY } from './useNotes';
import { changesNotes } from '../lib/toolStyle';
import {
  StreamEvent,
  Thread,
  ThreadMessage,
  ThreadSummary,
  ToolCall,
} from '../types/threads';

export const THREADS_QUERY_KEY = ['threads'];

export const threadQueryKey = (id: number) => ['threads', id];

export const useThreads = () => {
  const queryClient = useQueryClient();

  const { data: threads, isLoading } = useQuery<ThreadSummary[]>({
    queryKey: THREADS_QUERY_KEY,
    queryFn: threadsApi.list,
    staleTime: 30_000,
  });

  // `exact: true` is critical: without it the fuzzy match also invalidates
  // `['threads', <id>]` entries, which triggers a refetch that can race with
  // `setQueryData` and (on replicated DBs) roll the cache back to stale state.
  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: THREADS_QUERY_KEY, exact: true });

  const createMutation = useMutation({
    mutationFn: (title?: string | null) => threadsApi.create(title),
    onSuccess: (thread) => {
      invalidateList();
      queryClient.setQueryData<Thread>(threadQueryKey(thread.id), thread);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => threadsApi.delete(id),
    onSuccess: (_, id) => {
      invalidateList();
      queryClient.removeQueries({ queryKey: threadQueryKey(id) });
    },
  });

  return {
    threads: threads ?? [],
    isLoading,
    createThread: createMutation.mutateAsync,
    deleteThread: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

const nowIso = () => new Date().toISOString();

const emptyThread = (id: number, createdAt: string): Thread => ({
  id,
  title: null,
  created_at: createdAt,
  updated_at: createdAt,
  messages: [],
});

const mapMessages = (
  thread: Thread,
  fn: (msg: ThreadMessage) => ThreadMessage,
): Thread => ({ ...thread, messages: thread.messages.map(fn) });

const mapMessage = (
  thread: Thread,
  id: number,
  fn: (msg: ThreadMessage) => ThreadMessage,
): Thread =>
  mapMessages(thread, (m) => (m.id === id ? fn(m) : m));

/** Paces incoming text so heavy chunks from the model trickle smoothly into
 *  the UI at a steady, typewriter-ish rate. */
interface TextPacer {
  push: (text: string) => void;
  /** Mark no more text is coming; drain remaining at the paced rate. */
  complete: () => void;
  /** Dump any buffered text immediately (used on error / teardown). */
  flushNow: () => void;
}

const TICK_MS = 24;
const DRAIN_WINDOW_MS = 350;

function createTextPacer(
  emit: (chunk: string) => void,
): TextPacer {
  let buffer = '';
  let done = false;
  let timer: ReturnType<typeof setInterval> | null = null;

  const stop = () => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const release = (n: number) => {
    if (n <= 0 || buffer.length === 0) return;
    const chars = buffer.slice(0, n);
    buffer = buffer.slice(n);
    emit(chars);
  };

  const tick = () => {
    if (buffer.length === 0) {
      if (done) stop();
      return;
    }
    // Aim to empty the current buffer within DRAIN_WINDOW_MS, but always
    // release at least one char so slow streams still animate.
    const target = Math.max(
      1,
      Math.ceil(buffer.length / Math.max(1, DRAIN_WINDOW_MS / TICK_MS)),
    );
    release(target);
  };

  const ensureTimer = () => {
    if (timer === null) timer = setInterval(tick, TICK_MS);
  };

  return {
    push(text) {
      if (!text) return;
      buffer += text;
      ensureTimer();
    },
    complete() {
      done = true;
      ensureTimer();
    },
    flushNow() {
      release(buffer.length);
      done = true;
      stop();
    },
  };
}

export const useThread = (threadId: number | null) => {
  const queryClient = useQueryClient();

  const { data: thread, isLoading } = useQuery<Thread>({
    queryKey: threadId ? threadQueryKey(threadId) : ['threads', 'none'],
    queryFn: () => threadsApi.get(threadId as number),
    enabled: threadId !== null,
    staleTime: 15_000,
  });

  const updateThread = (
    id: number,
    fn: (prev: Thread) => Thread,
  ) =>
    queryClient.setQueryData<Thread | undefined>(threadQueryKey(id), (prev) =>
      prev ? fn(prev) : fn(emptyThread(id, nowIso())),
    );

  const streamMutation = useMutation({
    mutationFn: async ({ id, content }: { id: number; content: string }) => {
      const tempUserId = -Date.now();
      let currentAssistantId: number | null = null;
      const pacers = new Map<number, TextPacer>();

      const ensurePacer = (assistantId: number): TextPacer => {
        let pacer = pacers.get(assistantId);
        if (!pacer) {
          pacer = createTextPacer((chunk) => {
            updateThread(id, (prev) =>
              mapMessage(prev, assistantId, (m) => ({
                ...m,
                content: (m.content ?? '') + chunk,
              })),
            );
          });
          pacers.set(assistantId, pacer);
        }
        return pacer;
      };

      // Optimistic user message appears immediately.
      const optimisticUser: ThreadMessage = {
        id: tempUserId,
        role: 'user',
        content,
        created_at: nowIso(),
        tool_calls: [],
      };
      updateThread(id, (prev) => ({
        ...prev,
        messages: [...prev.messages, optimisticUser],
        updated_at: optimisticUser.created_at,
      }));

      try {
        for await (const event of threadsApi.streamMessage(id, content) as AsyncGenerator<StreamEvent>) {
          switch (event.type) {
            case 'user_saved':
              updateThread(id, (prev) =>
                mapMessage(prev, tempUserId, (m) => ({
                  ...m,
                  id: event.id,
                  created_at: event.created_at,
                })),
              );
              break;

            case 'assistant_start': {
              currentAssistantId = event.id;
              const placeholder: ThreadMessage = {
                id: event.id,
                role: 'assistant',
                content: '',
                created_at: nowIso(),
                tool_calls: [],
              };
              updateThread(id, (prev) => ({
                ...prev,
                messages: [...prev.messages, placeholder],
              }));
              ensurePacer(event.id);
              break;
            }

            case 'text_delta': {
              const aid = currentAssistantId;
              if (aid === null) break;
              ensurePacer(aid).push(event.text);
              break;
            }

            case 'tool_use': {
              const aid = currentAssistantId;
              if (aid === null) break;
              const newCall: ToolCall = {
                id: event.id,
                tool_use_id: event.tool_use_id,
                tool_name: event.tool_name,
                params: event.params,
                result: null,
                is_error: false,
                created_at: nowIso(),
                completed_at: null,
              };
              updateThread(id, (prev) =>
                mapMessage(prev, aid, (m) => ({
                  ...m,
                  tool_calls: [...m.tool_calls, newCall],
                })),
              );
              break;
            }

            case 'tool_result': {
              // First, find the matching call so we know its tool_name before
              // applying the result; title_thread needs special reconciliation.
              let matchedCall: ToolCall | undefined;
              updateThread(id, (prev) => {
                const next = mapMessages(prev, (m) => ({
                  ...m,
                  tool_calls: m.tool_calls.map((tc) => {
                    if (tc.tool_use_id !== event.tool_use_id) return tc;
                    matchedCall = tc;
                    return {
                      ...tc,
                      result: event.result,
                      is_error: event.is_error,
                      completed_at: event.completed_at,
                    };
                  }),
                }));
                if (
                  matchedCall &&
                  changesNotes(matchedCall.tool_name) &&
                  !event.is_error
                ) {
                  queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEY });
                }
                // Reflect thread title changes in cache immediately so the
                // sidebar + header pick them up mid-stream.
                if (
                  matchedCall?.tool_name === 'title_thread' &&
                  !event.is_error &&
                  typeof event.result?.title === 'string'
                ) {
                  const title = event.result.title as string;
                  queryClient.setQueryData<ThreadSummary[] | undefined>(
                    THREADS_QUERY_KEY,
                    (list) =>
                      list?.map((t) => (t.id === id ? { ...t, title } : t)),
                  );
                  return { ...next, title };
                }
                return next;
              });
              break;
            }

            case 'assistant_end':
              // No direct content write: the pacer owns the buffer and will
              // finish draining on its own tick schedule. If the backend's
              // final content differs (shouldn't), it'll reconcile via the
              // next query refetch.
              pacers.get(event.id)?.complete();
              break;

            case 'done':
              break;

            case 'error':
              throw new Error(event.message);
          }
        }
      } catch (e) {
        // On failure, dump any queued text so the user sees whatever arrived
        // before the error rather than trailing off mid-sentence.
        pacers.forEach((p) => p.flushNow());
        throw e;
      } finally {
        queryClient.invalidateQueries({ queryKey: THREADS_QUERY_KEY, exact: true });
      }
    },
  });

  return {
    thread,
    isLoading,
    postMessage: streamMutation.mutateAsync,
    isPosting: streamMutation.isPending,
    postError: streamMutation.error,
  };
};
